import { AntiFraudLockScreen } from "@/components/AntiFraudLockScreen";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Swal from "sweetalert2";
import { getDeviceId, getOldDeviceId, DEVICE_ID_KEY } from "@/utils/deviceId";
interface Profile {
 id: string;
 user_id: string;
 full_name: string | null;
 avatar_url: string | null;
 phone: string | null;
 birth_date: string | null;
 gender: string | null;
 is_player: boolean;
 is_vaqueiro?: boolean;
 is_motocross?: boolean;
 is_noiva?: boolean;
 is_vip?: boolean;
 vip_until?: string | null;
 team_1?: string | null;
 number_1?: string | null;
 team_2?: string | null;
 number_2?: string | null;
 team_3?: string | null;
 number_3?: string | null;
 points: number | null;
 level: number | null;
 last_seen_at: string | null;
 face_embedding: number[] | null;
 created_at: string;
 email?: string | null;
 primary_device_id?: string | null;
}

interface AuthContextType {
 session: Session | null;
 user: User | null;
 profile: Profile | null;
 isAdmin: boolean;
 isSuperAdmin: boolean;
 loading: boolean;
 signOut: () => Promise<void>;
 refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
 session: null,
 user: null,
 profile: null,
 isAdmin: false,
 isSuperAdmin: false,
 loading: true,
 signOut: async () => {},
 refreshProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
 const [session, setSession] = useState<Session | null>(null);
 const [user, setUser] = useState<User | null>(null);
 const [profile, setProfile] = useState<Profile | null>(null);
 const [isAdmin, setIsAdmin] = useState(false);
 const [isSuperAdmin, setIsSuperAdmin] = useState(false);
 const [loading, setLoading] = useState(true);

  const signOut = async () => {
    console.log("[AuthContext] 🚪 Iniciando processo de logout...");
    try {
      const theme = localStorage.getItem('theme');
      const deviceId = localStorage.getItem(DEVICE_ID_KEY); 
      const lastUser = localStorage.getItem('last_logged_user');
      
      localStorage.clear();
      
      if (theme) localStorage.setItem('theme', theme);
      if (deviceId) localStorage.setItem(DEVICE_ID_KEY, deviceId);
      if (lastUser) localStorage.setItem('last_logged_user', lastUser);

      await Promise.race([
        supabase.auth.signOut(),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), 2000))
      ]).catch(e => console.warn("[AuthContext] Server signout failed or timed out:", e));

    } catch (err) {
      console.error("[AuthContext] Erro durante o logout:", err);
    } finally {
      setSession(null);
      setUser(null);
      setProfile(null);
      setIsAdmin(false);
      setLoading(false);
      console.log("[AuthContext] ✅ Logout local concluído.");
    }
  };

  const fetchProfile = async (userId: string, emailStr?: string) => {
    const userEmail = (emailStr || user?.email || '').toLowerCase().trim();
    const isHardcodedAdmin = userEmail === 'hudsonraonefotografia@gmail.com' || userEmail === 'hudsonraone@gmail.com';
    
    if (isHardcodedAdmin) {
      console.log(`[AuthContext] ⭐ Hardcoded Admin detected: ${userEmail}`);
      setIsAdmin(true);
      setIsSuperAdmin(true);
    }

    console.log(`[AuthContext] 🟡 INICIANDO fetchProfile para ${userId}...`);
    try {
      const [profileResponse, currentDeviceId, roleResponse] = await Promise.all([
        supabase.from("profiles").select("*").eq("user_id", userId).single(),
        getDeviceId(),
        supabase.from("user_roles").select("role").eq("user_id", userId)
      ]);

      const { data, error } = profileResponse;
        
      if (error) {
        if (error.code === 'PGRST116') {
          console.warn(`[AuthContext] ⚠️ Perfil não encontrado para ${userId}`);
        } else {
          console.error("[AuthContext] ❌ Erro ao buscar perfil:", error);
          throw error;
        }
      }
      
      if (data) {
        const { data: roleData } = roleResponse;
        const hasAdminRole = roleData?.some((r) => r.role === "admin");
        const finalIsAdmin = hasAdminRole || isHardcodedAdmin;

        if (!finalIsAdmin) {
          // Lógica de migração (aparelhos velhos para hardware hash)
          const oldLocalId = getOldDeviceId();
          let dbDeviceId = data.primary_device_id;
          
          if (dbDeviceId && dbDeviceId.includes('-') && dbDeviceId === oldLocalId) {
             dbDeviceId = null; 
          }

          if (!dbDeviceId) {
            await supabase.from("profiles").update({ primary_device_id: currentDeviceId }).eq("user_id", userId);
            data.primary_device_id = currentDeviceId;
          } else if (dbDeviceId !== currentDeviceId) {
            console.warn("[AuthContext] 🚨 Aparelho não autorizado. Bloqueando acesso.");
            
            Swal.fire({
              title: "Acesso Bloqueado",
              text: "Sua conta já está conectada em outro aparelho. Por segurança, não é permitido compartilhar a conta ou usar múltiplos dispositivos.",
              icon: "error",
              confirmButtonText: "Entendi",
              confirmButtonColor: "#FF6A00",
              background: "#1c1c1e",
              color: "#fff",
            });
            
            await signOut();
            return; 
          }
        }

        setProfile(data || null);
 if (data) {
 localStorage.setItem("last_logged_user", JSON.stringify({
 name: data.full_name || "Usuário",
 avatar: data.avatar_url,
 email: userEmail
 }));
 }

        

        console.log(`[AuthContext] 🛡️ Verificação de Admin: DB=${hasAdminRole}, Hardcoded=${isHardcodedAdmin}`);
        setIsAdmin(finalIsAdmin);
      }
    } catch (err: any) {
      console.error("[AuthContext] ❌ Falha crítica no fetchProfile:", err);
    } finally {
      setLoading(false);
    }
  };

 const refreshProfile = async () => {
 if (user) await fetchProfile(user.id, user.email);
 };

 useEffect(() => {
 if (!user) return;
 const intervalId = setInterval(async () => {
 await supabase.from("profiles").update({ last_seen_at: new Date().toISOString() }).eq("user_id", user.id);
 }, 60000); // Update every minute while active
 return () => clearInterval(intervalId);
 }, [user]);

  useEffect(() => {
    // Failsafe: Ensure loading is never stuck
    const loadingTimeout = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn("[AuthContext] 🕒 Tempo limite de carregamento atingido (10s). Forçando renderização para evitar tela travada.");
        }
        return false;
      });
    }, 10000);

    let mounted = true;

 const initializeAuth = async () => {
 try {
 // Busca a sessão de forma síncrona/segura antes de liberar a tela
 const { data: { session: currentSession } } = await supabase.auth.getSession();
 
 if (mounted && currentSession) {
 setSession(currentSession);
 setUser(currentSession.user);
 
 const userEmail = (currentSession.user.email || '').toLowerCase().trim();
 const isHardcodedAdmin = userEmail === 'hudsonraonefotografia@gmail.com' || userEmail === 'hudsonraone@gmail.com';
 if (isHardcodedAdmin) setIsAdmin(true);
 
 await fetchProfile(currentSession.user.id, currentSession.user.email);
 } else if (mounted) {
 setLoading(false);
 }
 } catch (err) {
 console.error("[AuthContext] Erro ao buscar sessão inicial:", err);
 if (mounted) setLoading(false);
 }
 };

 initializeAuth();

 const { data: { subscription } } = supabase.auth.onAuthStateChange(
 async (event, session) => {
 console.log(`[AuthContext] Auth Event: ${event}`, !!session);
 
 if (!mounted) return;

 setSession(session);
 const currentUser = session?.user ?? null;
 setUser(currentUser);
 
 if (currentUser) {
 const userEmail = (currentUser.email || '').toLowerCase().trim();
 const isHardcodedAdmin = userEmail === 'hudsonraonefotografia@gmail.com' || userEmail === 'hudsonraone@gmail.com';
 if (isHardcodedAdmin) {
 console.log(`[AuthContext] ⭐ Hardcoded Admin detected immediately: ${userEmail}`);
 setIsAdmin(true);
 }
 // Only fetch if not INITIAL_SESSION or SIGNED_IN (handled by initializeAuth)
 if (event !== 'INITIAL_SESSION' && event !== 'SIGNED_IN') {
 await fetchProfile(currentUser.id, currentUser.email);
 } else if (event === 'SIGNED_IN' && !profile && !loading) {
 // Fallback if initializeAuth somehow missed it
 await fetchProfile(currentUser.id, currentUser.email);
 }
 } else {
 setProfile(null);
 setIsAdmin(false);
 setLoading(false);
 
 if (event === 'SIGNED_OUT' && window.location.pathname !== '/') {
 console.warn("[AuthContext] Logout inesperado detectado.");
 toast.error("Sua sessão foi encerrada.");
 }
 }
 }
 );

 return () => {
 mounted = false;
 clearTimeout(loadingTimeout);
 subscription.unsubscribe();
 };
 }, []);

  // Resolve travamento do Supabase quando o app fica inativo/suspenso por horas
  useEffect(() => {
    let lastActiveTime = Date.now();
    
    const handleVisibilityChange = () => {
      const now = Date.now();
      if (document.visibilityState === 'visible') {
        const inactiveDuration = now - lastActiveTime;
        // Se ficou inativo por mais de 30 minutos, recarrega para limpar estado
        if (inactiveDuration > 30 * 60 * 1000) {
          console.log("[AuthContext] App inativo por muito tempo. Preparando recarga segura...");
          
          const doReload = () => {
            console.log("[AuthContext] Recarregando app...");
            window.location.reload();
          };

          if (navigator.onLine) {
            // Pequeno atraso de 2 segundos para dar tempo do Wi-Fi/Rede Celular reconectar
            setTimeout(doReload, 2000);
          } else {
            // Se estiver offline, aguarda a rede voltar antes de recarregar
            window.addEventListener('online', doReload, { once: true });
          }
          return;
        }
        console.log("[AuthContext] App focado. Atualizando lastActiveTime.");
      } else {
        lastActiveTime = Date.now();
      }
    };
    
    window.addEventListener('visibilitychange', handleVisibilityChange);
    // Removemos o 'focus' para evitar loops desnecessários
    
    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const [fraudBlockData, setFraudBlockData] = useState<any>(null);

  useEffect(() => {
    const checkFraudBlock = async () => {
      try {
        const cacheKey = 'fraud_block_checked';
        const cacheTime = localStorage.getItem(cacheKey);
        // Só consulta o DB a cada 1 hora para evitar requisições desnecessárias (3600000ms = 1h)
        if (cacheTime && Date.now() - Number(cacheTime) < 3600000) {
            return;
        }

        const deviceId = await getDeviceId();
        const { data, error } = await supabase
          .from('fraud_blocks')
          .select('*')
          .eq('device_id', deviceId)
          .eq('status', 'blocked')
          .maybeSingle();
        
        if (data) {
          console.warn("[AuthContext] 🚨 Dispositivo bloqueado por fraude!");
          setFraudBlockData(data);
        } else {
          // Marca no cache que o aparelho está limpo por mais 1 hora
          localStorage.setItem(cacheKey, Date.now().toString());
        }
      } catch (err) {
        console.error("Erro ao checar fraud blocks", err);
      }
    };
    checkFraudBlock();
  }, []);

  if (fraudBlockData) {
    return <AntiFraudLockScreen blockData={fraudBlockData} onResolved={() => setFraudBlockData(null)} />;
  }

  return (
    <AuthContext.Provider value={{ session, user, profile, isAdmin, isSuperAdmin, loading, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};
